import React, { useEffect } from 'react'
import 'feather-icons'
import { withRouter } from 'react-router-dom'
import styled from 'styled-components'
import Link from '../components/Link'
import TokenLogo from '../components/TokenLogo'
import { AutoColumn } from '../components/Column'
import { formattedNum, formattedPercent, getPoolLink, getSwapLink } from '../utils'
import { useTokenData, useTokenTransactions, useTokenPairs } from '../contexts/TokenData'
import { TYPE } from '../Theme'
import { useColor } from '../hooks'
import WarningModal from '../components/WarningModal'
import SafetyIndicator from '../components/SafetyIndicator'
import { useSavedTokens, usePathDismissed } from '../contexts/LocalStorage'
import { BlockedWrapper, BlockedMessageWrapper } from '../components'
import { PlusCircle, Bookmark } from 'react-feather'
import { useListedTokens } from '../contexts/Application'
import { TOKEN_BLACKLIST, BLOCKED_WARNINGS, usedChain } from '../constants'
import { shortenAddress } from '../utils'
import { ExplorerDataType, getExplorerLink } from '../utils/etherscan'
import { useDarkModeManager } from '../contexts/LocalStorage'
import Header from '../components/Header'
import TransactionsSection from '../components/TransactionsSection'
import ChartStatsSection from '../components/ChartStatsSection'
import TopPairsSection from '../components/TopPairsSection'
import TokenInformationSection from '../components/TokenInformationSection'
import LocalLoader from '../components/LocalLoader'

const WarningGrouping = styled.div`
  opacity: ${({ disabled }) => disabled && '0.4'};
  pointer-events: ${({ disabled }) => disabled && 'none'};
`

const DarkPageContainer = styled.div`
  background: #16171c;
  min-height: 100vh;
  padding: 32px 48px 80px;
  margin-top: 40px;
  max-width: 1440px;
  margin-left: auto;
  margin-right: auto;
  width: 100%;

  @media screen and (max-width: 768px) {
    padding: 12px 20px 80px;
    margin-top: 40px;
  }
`

const BackButton = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  color: white;
  margin-bottom: 32px;

  &:hover {
    opacity: 0.8;
  }
`

function TokenPage({ address, history }) {
  const tokenData = useTokenData(address)

  const {
    id,
    name,
    symbol,
    priceUSD,
    oneDayVolumeUSD,
    totalLiquidityUSD,
    volumeChangeUSD,
    oneDayVolumeUT,
    volumeChangeUT,
    priceChangeUSD,
    liquidityChangeUSD,
    oneDayTxns,
    txnChange,
  } = tokenData || {}

  // Simplified loading state - only show loading if we have no token data at all
  const hasAnyTokenData = tokenData && Object.keys(tokenData).length > 0
  const isLoading = !hasAnyTokenData && address

  // Error state: check if we have an error flag or missing data after reasonable time
  const [hasError, setHasError] = React.useState(false)

  React.useEffect(() => {
    // Only set error if we explicitly get an error flag
    if (tokenData?.error) {
      setHasError(true)
      return
    }

    // Show a timeout error if we have an address but no data after 10 seconds
    if (address && isLoading) {
      const timer = setTimeout(() => {
        setHasError(true)
      }, 10000) // 10 seconds timeout

      return () => clearTimeout(timer)
    } else if (hasAnyTokenData) {
      // We have data, clear any error state
      setHasError(false)
    }
  }, [address, isLoading, hasAnyTokenData, tokenData])

  const [, toggleDarkMode] = useDarkModeManager()

  useEffect(() => {
    window.scrollTo({
      behavior: 'smooth',
      top: 0,
    })
    // Force dark mode for this page
    toggleDarkMode(true)
  }, [toggleDarkMode])

  // detect color from token
  const backgroundColor = useColor(id, symbol)

  const allPairs = useTokenPairs(address)

  // Convert pairs array to object format for PairList component
  const pairsForPairList = React.useMemo(() => {
    if (!allPairs || !Array.isArray(allPairs)) {
      return {}
    }

    const pairsObject = {}
    allPairs.forEach((pair) => {
      if (pair && pair.id) {
        pairsObject[pair.id] = pair
      }
    })
    return pairsObject
  }, [allPairs])

  // all transactions with this token
  const transactions = useTokenTransactions(address)

  // price
  const price = priceUSD ? formattedNum(priceUSD, true) : ''

  // volume
  const volume = formattedNum(!!oneDayVolumeUSD ? oneDayVolumeUSD : oneDayVolumeUT, true)

  const usingUtVolume = oneDayVolumeUSD === 0 && !!oneDayVolumeUT
  const volumeChange = formattedPercent(!usingUtVolume ? volumeChangeUSD : volumeChangeUT)

  // liquidity
  const liquidity = formattedNum(totalLiquidityUSD, true)
  const liquidityChange = formattedPercent(liquidityChangeUSD)

  // transactions
  const txnChangeFormatted = formattedPercent(txnChange)

  const [savedTokens, addToken, removeToken] = useSavedTokens()
  const listedTokens = useListedTokens()

  // Warning dismissal logic
  const [dismissed, markAsDismissed] = usePathDismissed(history?.location?.pathname || address)

  // Format symbol for display
  const formattedSymbol = symbol?.length > 16 ? symbol.slice(0, 16) + '...' : symbol

  if (TOKEN_BLACKLIST.includes(address)) {
    return (
      <BlockedWrapper>
        <BlockedMessageWrapper>
          <AutoColumn gap="1rem" justify="center">
            <TYPE.light style={{ textAlign: 'center' }}>
              {BLOCKED_WARNINGS[address] ?? `This token is not supported.`}
            </TYPE.light>
            <Link
              external={true}
              href={getExplorerLink(usedChain, address, ExplorerDataType.ADDRESS)}
            >{`More about ${shortenAddress(address)}`}</Link>
          </AutoColumn>
        </BlockedMessageWrapper>
      </BlockedWrapper>
    )
  }

  // Show loading state while waiting for token data
  if (isLoading) {
    return (
      <>
        <Header />
        <DarkPageContainer>
          <BackButton onClick={() => history.push('/home')}>
            <svg width="20" height="18" viewBox="0 0 20 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M8.5 16.5L1 9L8.5 1.5"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path d="M1 9H19" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span style={{ color: 'white', fontSize: '16px' }}>Back</span>
          </BackButton>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '400px',
              gap: '20px',
            }}
          >
            <LocalLoader />
            <TYPE.primary style={{ color: 'white', fontSize: '18px' }}>Loading token information...</TYPE.primary>
          </div>
        </DarkPageContainer>
      </>
    )
  }

  // Show error state if we can't load token data
  if (hasError) {
    return (
      <>
        <Header />
        <DarkPageContainer>
          <BackButton onClick={() => history.push('/home')}>
            <svg width="20" height="18" viewBox="0 0 20 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M8.5 16.5L1 9L8.5 1.5"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path d="M1 9H19" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span style={{ color: 'white', fontSize: '16px' }}>Back</span>
          </BackButton>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '400px',
              gap: '20px',
              textAlign: 'center',
            }}
          >
            <TYPE.primary style={{ color: '#ff8080', fontSize: '24px', fontWeight: 'bold' }}>
              Unable to load token information
            </TYPE.primary>
            <TYPE.light style={{ color: 'rgba(255,255,255,0.6)', fontSize: '16px', maxWidth: '400px' }}>
              We couldn't retrieve information for this token. This might be due to network issues or the token might
              not be available in our data sources.
            </TYPE.light>
            <Link
              external={true}
              href={getExplorerLink(usedChain, address, ExplorerDataType.ADDRESS)}
              style={{ color: '#45e560', textDecoration: 'underline' }}
            >
              View on block explorer: {shortenAddress(address)}
            </Link>
            <button
              onClick={() => {
                // Reset error state and force a refetch
                setHasError(false)
                // Force a hard refresh of Apollo cache for this token
                window.location.reload()
              }}
              style={{
                background: '#45e560',
                border: 'none',
                borderRadius: '12px',
                padding: '12px 24px',
                color: 'white',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                marginTop: '10px',
              }}
            >
              Try Again
            </button>
          </div>
        </DarkPageContainer>
      </>
    )
  }

  return (
    <>
      <Header />
      <DarkPageContainer>
        <BackButton onClick={() => history.push('/home')}>
          <svg width="20" height="18" viewBox="0 0 20 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M8.5 16.5L1 9L8.5 1.5"
              stroke="white"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path d="M1 9H19" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span style={{ color: 'white', fontSize: '16px' }}>Back</span>
        </BackButton>

        <WarningModal
          type={'token'}
          show={!dismissed && listedTokens && !listedTokens.includes(address)}
          setShow={markAsDismissed}
          address={address}
        />

        <WarningGrouping disabled={false}>
          {/* Header Section with Token Info and Actions */}
          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              marginBottom: '16px',
              width: '100%',
              flexWrap: 'wrap',
              gap: '16px',
            }}
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                alignItems: 'flex-start',
                flex: '1',
                minWidth: '280px',
              }}
            >
              <div style={{ display: 'flex', gap: '14px', alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ position: 'relative', width: '48px', height: '48px', flexShrink: '0' }}>
                  <TokenLogo
                    address={address}
                    size="48px"
                    style={{
                      position: 'absolute',
                      inset: '0',
                      maxWidth: 'none',
                      objectPosition: '50% 50%',
                      objectFit: 'cover',
                      pointerEvents: 'none',
                      width: '100%',
                      height: '100%',
                    }}
                  />
                </div>

                <div
                  style={{
                    fontFamily: 'Inter Display, sans-serif',
                    fontWeight: '700',
                    fontSize: 'clamp(24px, 5vw, 32px)',
                    color: 'white',
                    margin: '0',
                    lineHeight: '1',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formattedSymbol || symbol}
                </div>

                <div
                  style={{
                    fontFamily: 'Inter Display, sans-serif',
                    fontWeight: '500',
                    fontSize: 'clamp(14px, 3vw, 18px)',
                    color: 'rgba(255,255,255,0.5)',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formattedSymbol || symbol}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                flexShrink: '0',
                gap: '16px',
                width: 'auto',
              }}
            >
              <div style={{ position: 'relative', width: '20px', height: '20px' }}>
                {!savedTokens[address] ? (
                  <div onClick={() => addToken(address, symbol)} style={{ cursor: 'pointer' }}>
                    <PlusCircle style={{ color: 'white', width: '20px', height: '20px' }} />
                  </div>
                ) : (
                  <div onClick={() => removeToken(address)} style={{ cursor: 'pointer' }}>
                    <Bookmark style={{ color: 'white', width: '20px', height: '20px' }} />
                  </div>
                )}
              </div>

              <Link href={getPoolLink(address)} target="_blank">
                <div
                  style={{
                    background: '#222328',
                    boxSizing: 'border-box',
                    display: 'flex',
                    gap: '12px',
                    height: '50px',
                    alignItems: 'center',
                    padding: '12px 24px',
                    borderRadius: '50px',
                    cursor: 'pointer',
                    border: 'none',
                  }}
                >
                  <div
                    style={{
                      fontFamily: 'Inter Display, sans-serif',
                      fontWeight: '500',
                      fontSize: 'clamp(14px, 2.5vw, 16px)',
                      color: 'white',
                      whiteSpace: 'nowrap',
                      lineHeight: '1.4',
                    }}
                  >
                    Add Liquidity
                  </div>
                </div>
              </Link>

              <Link href={getSwapLink(address)} target="_blank">
                <div
                  style={{
                    background: 'white',
                    boxSizing: 'border-box',
                    display: 'flex',
                    gap: '12px',
                    height: '50px',
                    alignItems: 'center',
                    padding: '12px 24px',
                    borderRadius: '50px',
                    cursor: 'pointer',
                    border: 'none',
                  }}
                >
                  <div
                    style={{
                      fontFamily: 'Inter Display, sans-serif',
                      fontWeight: '500',
                      fontSize: 'clamp(14px, 2.5vw, 16px)',
                      color: '#16171c',
                      whiteSpace: 'nowrap',
                      lineHeight: '1.4',
                    }}
                  >
                    Trade
                  </div>
                </div>
              </Link>
            </div>
          </div>

          {/* Price and Change Section */}
          <div
            style={{
              display: 'flex',
              gap: '16px',
              alignItems: 'center',
              marginBottom: '32px',
              flexWrap: 'wrap',
            }}
          >
            <div
              style={{
                fontFamily: 'Inter Display, sans-serif',
                fontWeight: '700',
                fontSize: 'clamp(24px, 6vw, 32px)',
                color: 'white',
                lineHeight: '1',
              }}
            >
              {price}
            </div>

            {priceChangeUSD && (
              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <div
                  style={{
                    fontFamily: 'Inter Display, sans-serif',
                    fontWeight: '600',
                    fontSize: 'clamp(16px, 3vw, 18px)',
                    color: priceChangeUSD > 0 ? '#45e560' : '#ff8080',
                    lineHeight: 'normal',
                  }}
                >
                  {priceChangeUSD > 0 ? '+' : ''}
                  {priceChangeUSD.toFixed(2)}%
                </div>
              </div>
            )}
          </div>

          {/* Chart and Stats Section */}
          <ChartStatsSection
            address={address}
            backgroundColor={backgroundColor}
            price={price}
            liquidity={liquidity}
            liquidityChange={liquidityChange}
            liquidityChangeUSD={liquidityChangeUSD}
            volume={volume}
            volumeChange={volumeChange}
            volumeChangeUSD={volumeChangeUSD}
            oneDayTxns={oneDayTxns}
            txnChange={txnChangeFormatted}
            txnChangeRaw={txnChange}
          />
          <TopPairsSection pairs={pairsForPairList} />
          {/* Transactions Section */}
          <TransactionsSection transactions={transactions} backgroundColor="transparent" />
          <TokenInformationSection symbol={symbol} name={name} address={address} />
        </WarningGrouping>

        {/* Safety Indicator - Always visible floating button */}
        <SafetyIndicator type="token" address={address} />
      </DarkPageContainer>
    </>
  )
}

export default withRouter(TokenPage)
